"use client"

import React, { KeyboardEvent, useEffect, useRef, useState } from "react"
import { useRouter } from "next/navigation"
import { Command } from "cmdk"
import TC_Wrapper from "react-tag-commander"

import { SearchItem, searchItems } from "@/lib/azureSearchClient"
import { cn } from "@/lib/utils"
import { Button, RemixIcon } from "@/components/ui/atoms"

import { SearchBarSkeleton } from "./SearchBar.skeleton"

type TSearchBarProps = {
  className?: string
  searchlabel: string
  characterLimit?: number
  isOpen: boolean
  toggleSearch: () => void
  setIsSearchOpen: (isOpen: boolean) => void
  inputValue: string
}

const SearchBar = ({
  className,
  searchlabel,
  isOpen,
  toggleSearch,
  setIsSearchOpen,
}: TSearchBarProps) => {
  const router = useRouter()
  const inputRef = useRef<HTMLInputElement>(null)
  const wrapper = TC_Wrapper.getInstance()
  const clickingResult = useRef(false) // Ref to track if a result is being clicked

  const [input, setInput] = useState<string>("")
  const [results, setResults] = useState<SearchItem[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [active, setActive] = useState<boolean>()
  const listDisplayed = input.length > 0 && results.length > 0
  const handleInputChange = (newValue: string) => {
    setInput(newValue)
  }

  useEffect(() => {
    if (!isOpen || !inputRef.current) {
      setInput("")
    }
  }, [isOpen])

  const handleSearch = async (input: string) => {
    const searchQuery = await searchItems(input)
    const searchResults: SearchItem[] = []
    for await (const result of searchQuery.results) {
      searchResults.push(result.document)
    }
    setResults(searchResults)
    setLoading(false)
  }

  const handleSelect = (value: string, result: SearchItem) => {
    wrapper.triggerEvent("click", value, {
      click: result.Title,
      ise_keyword: input,
      ise_engine: "autocomplete",
      click_chapter1: result.Path,
      click_chapter2: "",
      click_chapter3: "",
      click_profil: "",
      click_tag: "",
      click_type: "",
    })
    router.push(`/${value}`)
  }

  const handleMoreResults = (value: string) => {
    router.push(`${value}?search=${input}`)
    toggleSearch()
    setIsSearchOpen(false)
    setInput("")
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault()
      handleMoreResults("/search")
      setInput("")
    }
  }

  const handleSearchWrapper = async (search: string) => {
    const loadingTimeout = setTimeout(() => {
      setLoading(true)
    }, 1500)

    if (inputRef.current) {
      handleInputChange(inputRef.current.value)
    }

    if (inputRef?.current?.value) {
      await handleSearch(search)
    }
    clearTimeout(loadingTimeout)
    setLoading(false)
  }

  return (
    <>
      <div
        className={cn(
          "search-bar align-center ml-auto flex transition-all",
          isOpen ? "w-full" : null,
          className
        )}
      >
        <div
          className={cn(
            isOpen
              ? input
                ? "text-input flex w-full rounded-400 border-0 bg-neutral-200 p-4 outline outline-2 outline-neutral-300 transition-all"
                : "text-input flex w-full rounded-400 border-0 bg-neutral-200 p-4 outline outline-2 outline-neutral-300 transition-all"
              : "hidden",
            active ? "outline-secondary-500" : ""
          )}
        >
          <Command shouldFilter={false} label="Command Menu">
            <Command.Input
              ref={inputRef}
              placeholder={searchlabel}
              className={cn(
                "text-input--input bg-neutral-200",
                isOpen ? "" : ""
              )}
              onValueChange={handleSearchWrapper}
              onKeyDown={handleKeyDown}
              value={input}
              onFocus={() => setActive(true)}
              onBlur={() => {
                if (!clickingResult.current) {
                  setActive(false)
                  setIsSearchOpen(false)
                  setInput("")
                }
              }}
            />
            <Command.List
              className={cn(
                "paragraph-m z-30 shadow-400",
                listDisplayed ? "" : "hidden"
              )}
            >
              {loading ? (
                <SearchBarSkeleton />
              ) : (
                <>
                  <Command.Group
                    heading="Résultats"
                    className="max-h-[450px] px-7 pt-6 xl:mx-[26px] xl:px-17  2xl:mx-[90px]"
                  >
                    {results.slice(0, 5).map((result) => {
                      return (
                        <Command.Item
                          onMouseDown={() => (clickingResult.current = true)}
                          onMouseUp={() => (clickingResult.current = false)}
                          onSelect={(value) => handleSelect(value, result)}
                          key={result.Path}
                          value={result.Path ?? ""}
                        >
                          {result.Title}
                        </Command.Item>
                      )
                    })}
                  </Command.Group>
                  <Command.Group className="all-results p-7 xl:px-17">
                    <Command.Item
                      className="pb-0 underline lg:mr-6 xl:mx-[26px] 2xl:mx-[90px]"
                      value={"/search"}
                      onMouseDown={() => (clickingResult.current = true)}
                      onMouseUp={() => (clickingResult.current = false)}
                      onSelect={(value) => handleMoreResults(value)}
                      keywords={[]}
                    >
                      Voir tous les résultats
                      <RemixIcon
                        name={"ArrowRight"}
                        size="md"
                        className="ml-3"
                      />
                    </Command.Item>
                  </Command.Group>
                </>
              )}
            </Command.List>
          </Command>
          <div className="ml-auto flex items-center">
            <RemixIcon
              name="CloseCircleFill"
              size="sm"
              className={cn(
                "shrink-0 cursor-pointer fill-current text-neutral-500",
                input ? "" : "hidden"
              )}
              onClick={() => setInput("")}
              onMouseDown={() => (clickingResult.current = true)}
              onMouseUp={() => (clickingResult.current = false)}
            />
            <div
              className={cn(
                "divider mx-5 hidden",
                input ? "xl:block" : "hidden"
              )}
            ></div>
            <RemixIcon
              name="Search"
              size="sm"
              className={cn(
                "shrink-0 cursor-pointer fill-current text-neutral-500",
                input ? "hidden xl:block" : ""
              )}
              onMouseDown={() => (clickingResult.current = true)}
              onMouseUp={() => (clickingResult.current = false)}
              onClick={() => handleMoreResults("/search")}
            />
          </div>
        </div>
        <div
          className={cn(
            isOpen
              ? "hidden"
              : "mr-3 flex aspect-square items-center justify-between rounded-400 bg-neutral-200 outline outline-1 outline-neutral-300 lg:bg-transparent lg:outline-none xl:mr-auto"
          )}
          role="button"
          tabIndex={0}
          aria-label={searchlabel}
          onClick={() => {
            setInput("")
            toggleSearch()
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault()
              setInput("")
              toggleSearch()
            }
          }}
        >
          <RemixIcon
            size="md"
            name={"Search"}
            className="hidden cursor-pointer lg:block"
          />
          <Button
            variant="ghost"
            iconLeft="Search"
            className=" cursor-pointer p-0 lg:hidden"
          />
        </div>
      </div>
    </>
  )
}

export { SearchBar, type TSearchBarProps }
